import { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, FileText, ExternalLink, Layers } from "lucide-react";

const TYPE_META = {
  section: { icon: Layers, label: "Section" },
  file: { icon: FileText, label: "File" },
  link: { icon: ExternalLink, label: "Link" },
};

export default function GlobalSearch({ sections, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const res = [];
    sections.forEach((s) => {
      const st = `${s.title || ""} ${s.description || ""}`.toLowerCase();
      if (st.includes(term)) res.push({ key: `s-${s.id}`, type: "section", label: s.title, sub: s.group, sectionId: s.id });
      (s.files || []).forEach((f) => {
        if ((f.file_name || "").toLowerCase().includes(term)) res.push({ key: `f-${f.id}`, type: "file", label: f.file_name, sub: s.title, sectionId: s.id });
      });
      (s.links || []).forEach((l) => {
        if (`${l.title || ""} ${l.url || ""} ${l.description || ""}`.toLowerCase().includes(term)) res.push({ key: `l-${l.id}`, type: "link", label: l.title || l.url, sub: s.title, sectionId: s.id });
      });
    });
    return res.slice(0, 25);
  }, [q, sections]);

  useEffect(() => { setActive(0); }, [q]);

  const choose = (r) => { onSelect(r.sectionId); setQ(""); setOpen(false); };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative w-full" data-testid="global-search">
      <div className="flex items-center gap-2 h-9 px-3 bg-[#0e0e0e] border border-[#222] rounded-md focus-within:border-[#4deb7a] transition-colors">
        <Search className="h-4 w-4 text-[#8f8f8f] shrink-0" strokeWidth={1.5} />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search sections, files, links…"
          className="flex-1 min-w-0 bg-transparent text-[13px] text-[#e8e8e8] placeholder:text-[#5a5a5a] focus:outline-none"
        />
        {q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setOpen(false); }} className="text-[#8f8f8f] hover:text-[#e8e8e8] shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div className="absolute left-0 right-0 mt-2 bg-[#0b0b0b] border border-[#222] rounded-md shadow-2xl shadow-black/60 max-h-[60vh] overflow-y-auto z-50" data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-5 text-[12px] text-[#8f8f8f] text-center">No matches for "{q.trim()}"</div>
          ) : (
            results.map((r, i) => {
              const Icon = TYPE_META[r.type].icon;
              return (
                <button
                  key={r.key}
                  data-testid={`search-result-${r.type}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(r)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-[#141414] last:border-0 transition-colors ${i === active ? "bg-[#141414]" : "hover:bg-[#0e0e0e]"}`}
                >
                  <span className="w-7 h-7 rounded flex items-center justify-center shrink-0 border border-[#2a2a2a] bg-[#111] text-[#4deb7a]">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] text-[#e8e8e8] truncate">{r.label}</span>
                    <span className="block font-mono-dm text-[10px] text-[#8f8f8f] truncate">{TYPE_META[r.type].label} · {r.sub}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
